import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../../../api/axiosInstance";
import { ChevronDown } from "lucide-react";

// --- Custom Premium Dropdown Component ---
const CustomDropdown = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));
  const displayText = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm text-slate-700 text-left font-medium"
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 z-50 w-full mt-1.5 bg-white border border-slate-100 shadow-xl rounded-2xl max-h-60 overflow-y-auto py-1.5 animate-fadeIn">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm transition-colors text-slate-700 hover:bg-slate-50 truncate font-medium ${
                String(opt.value) === String(value) ? "bg-blue-50/50 text-blue-600 font-bold" : ""
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ onFilter }) => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [selectedService, setSelectedService] = useState("All Services");
  const [loading, setLoading] = useState(false);

  // Fetch categories & services
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await axiosInstance.get("/users/vendors/filters");
        if (res.data.success) {
          setCategories(res.data.categories);
          setServices(res.data.services);
          setFilteredServices(res.data.services);
        }
      } catch (err) {
        console.error("❌ Error fetching filter data:", err);
      }
    };
    fetchFilters();
  }, []);

  // Filter services when category changes
  useEffect(() => {
    if (selectedCategory === "All Categories") {
      setFilteredServices(services);
    } else {
      const filtered = services.filter(
        (s) => s.categoryId === selectedCategory
      );
      setFilteredServices(filtered);
    }
    setSelectedService("All Services"); // reset selected service
  }, [selectedCategory, services]);

  // Fetch vendors based on filters
  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== "All Categories")
        params.category = selectedCategory;
      if (selectedService !== "All Services")
        params.service = selectedService;
      if (searchText) params.search = searchText;

      const res = await axiosInstance.get("/users/vendors", { params });
      if (res.data.success) {
        onFilter(res.data.data);
      } else {
        onFilter([]);
      }
    } catch (err) {
      console.error("❌ Error fetching vendors:", err);
      onFilter([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-search when filters change
  useEffect(() => {
    const categorySelected = selectedCategory !== "All Categories";
    const serviceSelected = selectedService !== "All Services";
    const textPresent = searchText.trim().length > 0;

    if (categorySelected || serviceSelected || textPresent) {
      handleSearch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedService, searchText]);

  // Map category options for CustomDropdown
  const categoryOptions = [
    { value: "All Categories", label: "All Categories" },
    ...categories.map((cat) => ({ value: cat._id, label: cat.name })),
  ];

  // Map service options for CustomDropdown
  const serviceOptions = [
    { value: "All Services", label: "All Services" },
    ...filteredServices.map((srv) => ({ value: srv._id, label: srv.name })),
  ];

  return (
    <div className="py-6 px-6 border-b border-slate-100 bg-white">
      <div className="flex flex-col md:flex-row items-center gap-4 max-w-5xl mx-auto">
        
        {/* Search Input */}
        <div className="relative w-full md:w-2/5">
          <input
            type="text"
            placeholder="Search by name, city, category, or service..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="w-full pl-4 pr-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm text-slate-700 placeholder:text-slate-400 font-medium"
          />
        </div>

        {/* Category Dropdown */}
        <div className="w-full md:w-1/4">
          <CustomDropdown
            value={selectedCategory}
            onChange={setSelectedCategory}
            options={categoryOptions}
            placeholder="All Categories"
          />
        </div>

        {/* Service Dropdown */}
        <div className="w-full md:w-1/4">
          <CustomDropdown
            value={selectedService}
            onChange={setSelectedService}
            options={serviceOptions}
            placeholder="All Services"
          />
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          disabled={loading}
          className={`w-full md:w-auto px-8 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm ${
            loading
              ? "bg-slate-300 text-slate-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-[0.98]"
          }`}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </div>
    </div>
  );
};

export default FilterSection;

